// fallow-ignore-file code-duplication
import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	ApplicationRef,
	Component,
	ComponentRef,
	computed,
	createComponent,
	effect,
	EffectRef,
	ElementRef,
	EnvironmentInjector,
	inject,
	input,
	OnDestroy,
	OnInit,
	output,
	QueryList,
	runInInjectionContext,
	signal,
	Type,
	viewChild,
	ViewChildren,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	CardType,
	ImageCardConfig,
	AnalysisCardConfig,
} from '@framework/analytics/models/card-model';
import { Subscription } from 'rxjs';
import {
	GridItemHTMLElement,
	GridStack,
	GridStackNode,
	GridStackOptions,
} from 'gridstack';
import { error as loggerError, log, warn } from '@jepto/shared/utils/logger';
import { JeptoBaseCardComponent } from '../../cards/jepto-base-card.component';
import { JeptoChartCardComponent } from '../../cards/jepto-chart-card/jepto-chart-card.component';
import { reflowHighchartsPlotsInElement } from '../../../utils/reflow-highcharts-plots.util';
import { liftAncestorIdentityTransforms } from '../../../utils/lift-ancestor-identity-transforms.util';
import { patchGridStackDocumentScrollClipping } from '../../../utils/gridstack-document-scroll-clipping.patch';
import { JeptoHtmlCardComponent } from '../../cards/jepto-html-card/jepto-html-card.component';
import { JeptoMarkdownCardComponent } from '../../cards/jepto-markdown-card/jepto-markdown-card.component';
import { ChartCardConfig, ChartType } from '../../../models/chart-config.model';
import {
	type QueryCardDataResponse,
	ImageDataRefs,
} from '../../../models/card-model';
import type { TableCardConfig } from '../../../models/table-config.model';
import {
	CardList,
	LayoutChangeEvent,
	LayoutConfig,
} from '../../../models/layout.model';
import { ThemeConfig } from '../../../models/theme.model';
import { tryDecodeReportIdPathSegment } from '../../../utils/report-id-encoding.util';
import { CardService } from '../../../services/card.service';
import { DataService } from '../../../services/data.service';
import { CardRuntimeRefreshStateService } from '../../../services/card-runtime-refresh-state.service';
import { ThemeService } from '../../../services/theme.service';
import { GridStackConfigService } from '../../../services/grid-stack-config.service';
import { getCardGridSize } from '../../../utils/card-grid-size.util';
import { dedupeLayoutCardsByCardId } from '../../../utils/dedupe-layout-cards.util';
import { JeptoFilterCardComponent } from '../../cards/jepto-filter-card/jepto-filter-card.component';
import {
	ControlType,
	isFilterCardConfig,
	type FilterCardConfig,
} from '../../../models/card-model';
import {
	isDimensionFilterCardData,
	type FilterCardRuntimeData,
} from '../../../models/page-filter.model';
import { dimensionFilterDataFingerprint } from '../../editors/jepto-dropdown-filter-card-editor/services/filter-preview-data.util';
import {
	compactFilterLayoutMinRowHeight,
	compactFilterLayoutRowHeight,
	isCompactFilterControlType,
	normalizeCompactFilterLayoutCard,
} from '../../../utils/filter-control-type.util';
import { mergeDimensionFilterPreviewData } from '../../../utils/dimension-filter-merge.util';
import { JeptoImageCardComponent } from '../../cards/jepto-image-card/jepto-image-card.component';
import { JeptoAnalysisCardComponent } from '../../cards/jepto-analysis-card/jepto-analysis-card.component';
import { JeptoTableCardComponent } from '../../cards/jepto-table-card/jepto-table-card.component';
import type { DateRangeExpression } from '../../../models/date-range-expression.model';
import { changedCardTypeIds } from './card-type-remount-coordinator';

/** GridStack host for analytics/report cards — see `layout.mdx` (Storybook). */
@Component({
	selector: 'jepto-layout-container',
	templateUrl: './jepto-layout-container.component.html',
	styleUrls: ['./jepto-layout-container.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule],
})
export class JeptoLayoutContainerComponent
	implements OnInit, AfterViewInit, OnDestroy
{
	readonly layout = input.required<LayoutConfig>();

	readonly themeMode = input<'light' | 'dark' | 'auto'>('auto');

	readonly themeConfig = input<ThemeConfig>();

	readonly palette = input<string>('pal_jepto');

	/** Report view theme palette id (for chart cards with inheritsReportThemePalette). */
	readonly reportThemePaletteId = input<string | undefined>(undefined);

	readonly layoutOptions = input<Partial<GridStackOptions>>({});

	readonly userId = input<string>();

	/**
	 * Report scope for debug overlays. Prefer these over URL decoding so the
	 * Jepto app (`/clients/...`) does not treat route segments as Crockford report ids.
	 */
	readonly accountId = input<string | undefined>(undefined);

	readonly viewId = input<string | undefined>(undefined);

	readonly staticGrid = input<boolean>(false);

	readonly allowEmptyCharts = input<boolean>(false);

	/**
	 * Report-level default date range for date-range filter cards (view settings / getView).
	 * Passed through to {@link JeptoFilterCardComponent} in preview.
	 */
	readonly reportDateRangeExpression = input<
		DateRangeExpression | undefined
	>();

	private currentLayout = signal<LayoutConfig>({} as LayoutConfig);

	/** Palette id passed to cards: explicit report theme id, then themeConfig, then input default. */
	private readonly effectiveReportPaletteId = computed(() => {
		const explicit = this.reportThemePaletteId()?.trim();
		if (explicit) {
			return explicit;
		}
		const fromThemeConfig = this.themeConfig()?.palette?.trim();
		if (fromThemeConfig) {
			return fromThemeConfig;
		}
		return this.palette();
	});

	readonly layoutChange = output<LayoutChangeEvent>();

	readonly cardError = output<{ itemId: string; error: any }>();

	readonly cardEdit = output<string>();

	readonly cardDuplicate = output<string>();

	readonly cardSaveAsTemplate = output<string>();

	readonly cardDelete = output<string>();

	readonly filterScopeConfigure = output<string>();

	readonly filterDateRangeChange = output<DateRangeExpression>();

	private grid?: GridStack;
	private readonly gridStackHost =
		viewChild<ElementRef<HTMLElement>>('gridStackHost');
	private gridInitFrameId: number | null = null;
	private destroyed = false;
	private componentRefs = new Map<
		string,
		ComponentRef<JeptoBaseCardComponent>
	>();
	private cardEffectRefs = new Map<string, EffectRef[]>();
	private cardSubscriptions = new Map<string, Subscription>();
	private errorStates = new Map<string, boolean>();
	private lastSavedLayout: CardList[] = [];
	/** Removed on destroy so page switches do not stack conflicting grid rules in `head`. */
	private gridLayoutStyleEl: HTMLStyleElement | null = null;
	/** Restores ancestor transforms lifted for the current card pointer gesture. */
	private restoreLiftedTransforms: (() => void) | null = null;
	/** Pointer that started the lift; only its release ends it. */
	private liftedPointerId: number | null = null;
	private liftEndTimeoutId: ReturnType<typeof setTimeout> | null = null;

	private themeService = inject(ThemeService);
	private dataService = inject(DataService);
	private readonly cardRuntimeRefreshState = inject(
		CardRuntimeRefreshStateService
	);
	private cardService = inject(CardService);
	private appRef = inject(ApplicationRef);
	private environmentInjector = inject(EnvironmentInjector);
	private readonly gridStackConfig = inject(GridStackConfigService);

	constructor() {
		// Single pipeline for layout updates from any source (initial input, local
		// drag/resize echo, future websocket/remote updates). `applyLayout()` calls
		// `grid.load()` which GridStack itself diffs by `id` — existing widgets are
		// updated in place, new widgets fire `added` (we create a card component),
		// orphans fire `removed` (we destroy theirs). This is what fixed the grey
		// flash: positional echoes no longer destroy/recreate any component. The
		// one classification the container does itself: an existing `cardId` whose
		// `cardType` changed is destroyed/removed before the load so the `added`
		// path recreates it with the correct component type.
		effect(() => {
			const l = this.layout();
			if (!l) return;
			// Layout rows can omit `controlType` until the card config hydrates, and
			// compact filter sizing depends on it. Track those configs so the layout
			// is re-applied once the control type is known.
			for (const card of l.cards) {
				if (card.cardType === CardType.Filter && !card.controlType) {
					this.cardService.cardConfig(card.cardId)();
				}
			}
			this.currentLayout.set(l);
			// grid.load() fires `added` synchronously; card setup registers per-card
			// effects — must not run inside this parent effect (NG0602).
			if (this.grid) {
				queueMicrotask(() => {
					if (this.destroyed) return;
					this.applyLayout();
				});
			}
		});
		effect(() => {
			this.themeService.initFromViewConfig(this.themeMode());
		});
		effect(() => {
			this.staticGrid();
			this.syncGridEditability();
		});
		// Create an effect to handle signal updates for chart data
		this.effectRef = effect(() => {
			this.componentRefs.forEach((componentRef, cardId) => {
				const instance = componentRef.instance;
				if (instance instanceof JeptoChartCardComponent) {
					const chartInstance = instance as JeptoChartCardComponent;
					const config = chartInstance.config();
					const chartType = config?.chartType;
					if (chartType) {
						const cardDataSignal =
							this.dataService.cardDataSignal(cardId);
						const chartData = cardDataSignal();
						if (chartData && chartData.cardId === cardId) {
							componentRef.setInput('data', chartData);
						}
					}
				}
			});
		});
		runInInjectionContext(this.environmentInjector, () => {
			effect(() => {
				const loadingIds =
					this.cardRuntimeRefreshState.loadingCardIds();
				this.componentRefs.forEach((componentRef, cardId) => {
					const instance = componentRef.instance;
					if (
						instance instanceof JeptoChartCardComponent ||
						instance instanceof JeptoTableCardComponent
					) {
						componentRef.setInput(
							'isPreviewDataLoading',
							loadingIds.has(cardId)
						);
					}
					if (instance instanceof JeptoAnalysisCardComponent) {
						componentRef.setInput(
							'loading',
							loadingIds.has(cardId)
						);
					}
				});
			});
		});
		runInInjectionContext(this.environmentInjector, () => {
			effect(() => {
				this.themeService.getActiveThemeModeSignal()();
				const themeConfig =
					this.themeConfig() ?? this.themeService.getThemeConfig();
				if (themeConfig) {
					this.themeService.syncReportThemeCss(themeConfig);
					// Keep GridStack runtime spacing in sync when report settings updates card gap.
					if (this.grid) {
						const nextMargin = themeConfig.card.spacing.replace(
							'px',
							''
						);
						try {
							(
								this.grid as {
									margin?: (value: string) => void;
								}
							).margin?.(nextMargin);
						} catch {
							// Best-effort margin sync; CSS var still updates card spacing styles.
						}
					}
					this.applyResponsiveTypography(window.innerWidth);
				}
			});
		});
	}

	getDebugModeFromUrl(): boolean {
		if (typeof window === 'undefined') return false;
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.get('debug') === 'true';
	}

	/**
	 * Account id for the debug strip: input first, then analytics shared-report URL.
	 */
	getAccountIdDisplay(): string | null {
		const fromInput = this.accountId()?.trim();
		if (fromInput) return fromInput;
		return this.#decodeReportIdsFromPath()?.accountId ?? null;
	}

	/**
	 * Resolve raw view id for debug: input first, then shared-report decode,
	 * else current layout id (shown separately as layoutId in the strip).
	 */
	#resolveViewId(): string | null {
		const fromInput = this.viewId()?.trim();
		if (fromInput) return fromInput;

		const decoded = this.#decodeReportIdsFromPath();
		if (decoded?.viewId) return decoded.viewId;

		return this.currentLayout()?.layoutId || null;
	}

	getPageId(): string | null {
		if (typeof window === 'undefined')
			return this.currentLayout()?.pageId || null;

		// Try to extract pageId from URL path
		// URL format: /{reportId}/page/{pageId}
		const pageMatch = window.location.pathname.match(/\/page\/([^\/]+)/);
		if (pageMatch && pageMatch[1]) {
			return pageMatch[1];
		}

		// Fall back to layout.pageId
		return this.currentLayout()?.pageId || null;
	}

	getLayoutId(): string | null {
		return this.currentLayout()?.layoutId ?? null;
	}

	getViewIdDisplay(): string | null {
		const raw = this.#resolveViewId();
		if (!raw) return null;
		return raw.startsWith('view_') ? raw : `view_${raw}`;
	}

	getPageIdDisplay(): string | null {
		const raw = this.getPageId();
		if (!raw) return null;
		return raw.startsWith('page_') ? raw : `page_${raw}`;
	}

	/**
	 * Decode Crockford report id from the first path segment when it looks like
	 * an analytics shared-report URL. See {@link tryDecodeReportIdPathSegment}.
	 */
	#decodeReportIdsFromPath(): {
		accountId: string;
		viewId: string;
	} | null {
		if (typeof window === 'undefined') return null;

		const pathMatch = window.location.pathname.match(/^\/([^\/]+)/);
		if (!pathMatch?.[1]) {
			if (this.getDebugModeFromUrl()) {
				log(
					'[LayoutContainer] No reportId found in pathname:',
					window.location.pathname
				);
			}
			return null;
		}

		const decoded = tryDecodeReportIdPathSegment(pathMatch[1]);
		if (!decoded) {
			if (this.getDebugModeFromUrl()) {
				log(
					'[LayoutContainer] Path segment is not a report id:',
					pathMatch[1]
				);
			}
			return null;
		}

		if (this.getDebugModeFromUrl()) {
			log(
				'[LayoutContainer] Decoded reportId:',
				pathMatch[1],
				'→ accountId:',
				decoded.accountId,
				'viewId:',
				decoded.viewId
			);
		}
		return decoded;
	}

	readonly themeIdDisplay = this.themeService.themeIdDisplaySignal;

	// Component type mapping
	private readonly componentMap = new Map<
		string,
		Type<JeptoBaseCardComponent>
	>([
		[
			'chart',
			JeptoChartCardComponent as unknown as Type<JeptoBaseCardComponent>,
		],
		[
			'markdown',
			JeptoMarkdownCardComponent as unknown as Type<JeptoBaseCardComponent>,
		],
		[
			'html',
			JeptoHtmlCardComponent as unknown as Type<JeptoBaseCardComponent>,
		],
		[
			'image',
			JeptoImageCardComponent as unknown as Type<JeptoBaseCardComponent>,
		],
		[
			'filter',
			JeptoFilterCardComponent as unknown as Type<JeptoBaseCardComponent>,
		],
		[
			'analysis',
			JeptoAnalysisCardComponent as unknown as Type<JeptoBaseCardComponent>,
		],
		[
			'table',
			JeptoTableCardComponent as unknown as Type<JeptoBaseCardComponent>,
		],
	]);

	private effectRef: EffectRef;

	@ViewChildren(JeptoHtmlCardComponent)
	htmlCardComponents: QueryList<JeptoHtmlCardComponent>;

	/** GridStack's `columnOpts` breakpoints own column/layout responsiveness; this only keeps typography tokens in sync with viewport width. */
	private resizeHandler = () => {
		this.applyResponsiveTypography(window.innerWidth);
	};

	/**
	 * GridStack drags the card itself with `position: fixed` and measures any
	 * transformed ancestor's offset only once, at drag start. The report page's
	 * `.dialog-host-container` has `transform: translateZ(0)` (scopes CDK
	 * overlays), which makes `fixed` relative to that container, so when the
	 * page auto-scrolls the card scrolls away from the cursor and GridStack's
	 * clipping check stops the scroll. Lift identity transforms for the gesture
	 * so the card stays under the cursor and auto-scroll keeps going (JEP-10485).
	 */
	private gridPointerDownHandler = (event: PointerEvent) => {
		if (this.staticGrid() || event.button !== 0 || !this.grid) return;
		this.endLiftedTransforms();
		this.restoreLiftedTransforms = liftAncestorIdentityTransforms(
			this.grid.el
		);
		this.liftedPointerId = event.pointerId;
		window.addEventListener('pointerup', this.gridPointerEndHandler, true);
		window.addEventListener(
			'pointercancel',
			this.gridPointerEndHandler,
			true
		);
	};

	// Only the lifting pointer's release ends the gesture (not another finger).
	// No button filter: pointerup fires once all of that pointer's buttons are
	// up (chorded presses/releases are pointermove) and reports the last button
	// released, which can be non-primary. Deferred so GridStack's own
	// mouseup/dragstop runs with transforms still lifted.
	private gridPointerEndHandler = (event: PointerEvent) => {
		if (event.pointerId !== this.liftedPointerId) return;
		this.clearLiftEndTimeout();
		this.liftEndTimeoutId = setTimeout(() => this.endLiftedTransforms());
	};

	private clearLiftEndTimeout(): void {
		if (this.liftEndTimeoutId === null) return;
		clearTimeout(this.liftEndTimeoutId);
		this.liftEndTimeoutId = null;
	}

	private endLiftedTransforms(): void {
		// A pending end from an earlier gesture must not end a newer lift.
		this.clearLiftEndTimeout();
		this.liftedPointerId = null;
		window.removeEventListener(
			'pointerup',
			this.gridPointerEndHandler,
			true
		);
		window.removeEventListener(
			'pointercancel',
			this.gridPointerEndHandler,
			true
		);
		this.restoreLiftedTransforms?.();
		this.restoreLiftedTransforms = null;
	}

	private orientationHandler = () => {
		setTimeout(() => {
			this.applyResponsiveTypography(window.innerWidth);
		}, 100);
	};

	ngOnInit() {
		this.currentLayout.set(this.layout());
		// Initialize theme from layout's configured mode
		this.themeService.initFromViewConfig(this.themeMode());
	}

	ngAfterViewInit(): void {
		this.scheduleGridInit();
	}

	private scheduleGridInit(): void {
		this.cancelGridInitFrame();
		this.gridInitFrameId = requestAnimationFrame(() => {
			this.gridInitFrameId = null;
			if (this.destroyed) return;
			this.initGridStack();
		});
	}

	private cancelGridInitFrame(): void {
		if (this.gridInitFrameId != null) {
			cancelAnimationFrame(this.gridInitFrameId);
			this.gridInitFrameId = null;
		}
	}

	private getGridHostElement(): HTMLElement | undefined {
		return this.gridStackHost()?.nativeElement;
	}

	private initGridStack(): void {
		const host = this.getGridHostElement();
		if (!host) {
			loggerError(
				'[JepLayoutComponent] Failed to initialize grid: host element missing'
			);
			return;
		}

		const themeConfig = this.themeService.getThemeConfig();
		const cardSpacing = themeConfig.card.spacing.replace('px', '');

		const options = this.gridStackConfig.getOptions({
			margin: cardSpacing,
			staticGrid: this.staticGrid(),
			...this.layoutOptions(),
		});

		log('[JepLayoutComponent] Initializing grid with options:', {
			...options,
			cardSpacing,
			themeData: themeConfig.card,
		});

		this.gridLayoutStyleEl?.remove();
		const styleElement = document.createElement('style');
		styleElement.setAttribute('data-jepto-grid-layout', '');
		styleElement.textContent = `
        .grid-stack {
          background: transparent;
        }
        .grid-stack > .grid-stack-item {
          padding: 0;
        }
        .grid-stack-item-content {
          inset: calc(var(--card-spacing, ${cardSpacing}px) / 2) !important;
          width: auto !important;
          height: auto !important;
          border-radius: var(--card-border-radius);
          overflow: visible;
        }
        .grid-stack-item-content.jepto-filter-card--dropdown,
        .grid-stack-item-content.jepto-filter-card--date-range,
        .grid-stack-item-content.jepto-filter-card--search {
          bottom: auto !important;
          height: auto !important;
          max-height: none !important;
          background: transparent !important;
          box-shadow: none !important;
          border: none !important;
        }
      `;
		document.head.appendChild(styleElement);
		this.gridLayoutStyleEl = styleElement;

		patchGridStackDocumentScrollClipping();
		this.grid = GridStack.init(options, host);

		if (this.grid) {
			log('[JepLayoutComponent] Grid initialized:', {
				margin: this.grid.opts.margin,
				column: this.grid.opts.column,
				float: this.grid.opts.float,
			});

			// Wire `added` / `removed` before the first `grid.load()` so the
			// initial widgets fire `added`, which is where we create card components.
			this.setupGridEvents();
			this.applyLayout();
			this.syncGridEditability();
		} else {
			loggerError('[JepLayoutComponent] Failed to initialize grid');
		}
	}

	/** Keep GridStack drag/resize in sync with `staticGrid`. Column/layout responsiveness is owned by `columnOpts` breakpoints, not this method. */
	private syncGridEditability(): void {
		if (!this.grid) return;
		const isStatic = this.staticGrid();
		this.grid.setStatic(isStatic);
		if (!isStatic) {
			this.grid.enable();
		}
	}

	private setupGridEvents() {
		if (!this.grid) return;

		this.grid.on('dragstop', (event: Event, el: GridItemHTMLElement) => {
			this.handleLayoutChange('drag');
		});

		this.grid.on('resizestop', (event: Event, el: GridItemHTMLElement) => {
			reflowHighchartsPlotsInElement(el);
			this.handleLayoutChange('resize');
		});

		// `grid.load()` fires `added` for new widgets and `removed` for orphans.
		// Hooking Angular component lifecycle to these events keeps card components
		// alive across position-only layout updates (local resize echo, remote
		// collaborator move) — that's what eliminates the post-resize grey flash.
		// Same-id cardType transitions are remounted through this `added` path
		// too: `remountCardsWithChangedType()` removes the stale widget with the
		// `removed` event suppressed, then the load re-adds it here.
		this.grid.on('added', (_event: Event, nodes: GridStackNode[]) => {
			for (const node of nodes) {
				this.createCardForNode(node);
			}
		});

		this.grid.on('removed', (_event: Event, nodes: GridStackNode[]) => {
			for (const node of nodes) {
				const cardId = node.id as string | undefined;
				if (cardId) this.destroyCard(cardId);
			}
		});

		window.addEventListener('resize', this.resizeHandler);
		window.addEventListener('orientationchange', this.orientationHandler);
		this.grid.el.addEventListener(
			'pointerdown',
			this.gridPointerDownHandler,
			true
		);
	}

	private createCardForNode(node: GridStackNode): void {
		const cardId = node.id as string | undefined;
		if (!cardId) return;

		const item = this.currentLayout()?.cards.find(c => c.cardId === cardId);
		if (!item) {
			loggerError(
				`[JepLayoutComponent] 'added' for cardId ${cardId} with no matching CardList`
			);
			return;
		}

		const content = node.el?.querySelector(
			'.grid-stack-item-content'
		) as HTMLElement | null;
		if (!content) {
			loggerError(
				`[JepLayoutComponent] No content element for card ${cardId}`
			);
			return;
		}

		try {
			this.createAndConfigureComponent(item, content);
		} catch (error) {
			loggerError(
				`[JepLayoutComponent] Error creating component for ${cardId}:`,
				error
			);
			this.handleCardError(cardId, error);
		}
	}

	private destroyCard(cardId: string): void {
		this.destroyCardEffects(cardId);
		const sub = this.cardSubscriptions.get(cardId);
		if (sub) {
			sub.unsubscribe();
			this.cardSubscriptions.delete(cardId);
		}
		const ref = this.componentRefs.get(cardId);
		if (ref) {
			ref.destroy();
			this.componentRefs.delete(cardId);
		}
		this.errorStates.delete(cardId);
	}

	/** Preview-only: clients-report binds staticGrid to !isEdit(). */
	#forwardFilterDateRangeChangeWhenPreview(
		expression: DateRangeExpression
	): void {
		if (!this.staticGrid()) {
			return;
		}
		this.filterDateRangeChange.emit(expression);
	}

	private handleLayoutChange(changeType: 'drag' | 'resize') {
		if (!this.grid || this.staticGrid()) return; // Don't track changes in static mode

		const nodes = this.grid.engine.nodes;

		// Create full layout snapshot
		const fullLayout = this.currentLayout().cards.map(item => {
			const node = nodes.find(n => n.id === item.cardId);
			if (node) {
				return this.#normalizeCompactFilterLayoutItem({
					...item,
					x: node.x || 0,
					y: node.y || 0,
					w: node.w || item.w,
					h: node.h || item.h,
				});
			}
			return item;
		});

		// Find items that actually changed by comparing with last saved state
		const changedItemIds = nodes
			.map(node => node.id as string)
			.filter(id => {
				const node = nodes.find(n => n.id === id);
				const originalItem = this.lastSavedLayout.find(
					item => item.cardId === id
				);

				return (
					originalItem &&
					(node?.x !== originalItem.x ||
						node?.y !== originalItem.y ||
						node?.w !== originalItem.w ||
						node?.h !== originalItem.h)
				);
			});

		// Only emit if there are actual changes
		if (changedItemIds.length > 0) {
			// Create incremental changes for changed items only
			const incrementalChanges = changedItemIds.map(id => {
				const node = nodes.find(n => n.id === id);
				const originalItem = this.lastSavedLayout.find(
					item => item.cardId === id
				);
				return {
					cardId: id,
					changes: {
						from: {
							x: originalItem?.x || 0,
							y: originalItem?.y || 0,
							w: originalItem?.w || 0,
							h: originalItem?.h || 0,
						},
						to: {
							x: node?.x || 0,
							y: node?.y || 0,
							w: node?.w || 0,
							h: node?.h || 0,
						},
					},
				};
			});

			const layoutChangeData: LayoutChangeEvent = {
				previousLayout: this.lastSavedLayout,
				newLayout: fullLayout,
				changedItemIds,
				changes: {
					incremental: incrementalChanges,
					full: fullLayout,
					timestamp: new Date().toISOString(),
					changeType,
					userId: this.userId(),
				},
			};

			this.layoutChange.emit(layoutChangeData);
			this.lastSavedLayout = [...fullLayout];

			// Update the layout object with the new card positions
			this.currentLayout.set({
				...this.currentLayout(),
				cards: fullLayout,
			});
		}
	}

	private parseGridUnit(
		value: unknown,
		min: number,
		fallback: number
	): number {
		if (value === null || value === undefined) {
			return fallback;
		}
		if (typeof value === 'string' && value.trim() === '') {
			return fallback;
		}
		const n = typeof value === 'number' ? value : Number(value);
		if (!Number.isFinite(n)) {
			return fallback;
		}
		return Math.max(min, Math.round(n));
	}

	/** Dropdown/search/date-range filters use a fixed compact row span (no vertical resize). */
	#resolveLayoutRowHeight(item: CardList, normalizedH: number): number {
		const controlType = this.#resolveFilterControlTypeForLayoutItem(item);
		return compactFilterLayoutRowHeight(controlType) ?? normalizedH;
	}

	#normalizeCompactFilterLayoutItem(item: CardList): CardList {
		return normalizeCompactFilterLayoutCard(
			item,
			this.#resolveFilterControlTypeForLayoutItem(item)
		);
	}

	#resolveFilterControlTypeForLayoutItem(
		item: CardList
	): ControlType | undefined {
		if (item.controlType) {
			return item.controlType;
		}
		const storedConfig = this.cardService.cardConfig(item.cardId)();
		return isFilterCardConfig(storedConfig)
			? storedConfig.controlType
			: undefined;
	}

	private destroyCardEffects(cardId: string): void {
		const refs = this.cardEffectRefs.get(cardId);
		if (!refs?.length) return;
		for (const r of refs) {
			r.destroy();
		}
		this.cardEffectRefs.delete(cardId);
	}

	private destroyAllCardEffects(): void {
		this.cardEffectRefs.forEach(refs => {
			for (const r of refs) {
				r.destroy();
			}
		});
		this.cardEffectRefs.clear();
	}

	private normalizeGridCoordinates(card: CardList): {
		x: number;
		y: number;
		w: number;
		h: number;
	} {
		return {
			x: this.parseGridUnit(card.x, 0, 0),
			y: this.parseGridUnit(card.y, 0, 0),
			w: this.parseGridUnit(card.w, 1, 1),
			h: this.parseGridUnit(card.h, 1, 1),
		};
	}

	/**
	 * Narrow exception to the id-keyed GridStack diff: when an incoming layout
	 * keeps a `cardId` but changes its `cardType` (e.g. an authoritative
	 * `update_layout_cards` correcting an AI-generated chart shell to a table),
	 * updating the widget in place would leave the wrong Angular component
	 * mounted. Destroy that card and remove its widget here so the subsequent
	 * `grid.load()` re-adds it and the normal `added` lifecycle creates the
	 * correct component type.
	 *
	 * `removeWidget(el, true, false)` suppresses the `removed` event because
	 * `destroyCard()` already ran — firing it would duplicate lifecycle work.
	 *
	 * A node that exists but has no `el` cannot be removed from the grid, so
	 * its component must not be destroyed either: `grid.load()` would update
	 * the surviving widget in place and `added` would never fire to recreate
	 * the component. Such cards are returned as deferred; `applyLayout()`
	 * keeps their previous `cardType` in `lastSavedLayout` so the transition
	 * is re-detected and retried on the next apply, once a removable element
	 * exists. If no node exists at all, the old component is destroyed and
	 * `grid.load()` adds the widget, firing `added` to create the correct type.
	 *
	 * `lastSavedLayout` is empty on the first `applyLayout()` after grid init,
	 * so this intentionally remounts nothing on first paint.
	 *
	 * @returns cardIds whose remount was deferred because the grid node has no
	 * removable element yet.
	 */
	private remountCardsWithChangedType(nextCards: CardList[]): Set<string> {
		const deferredCardIds = new Set<string>();
		if (!this.grid) {
			return deferredCardIds;
		}

		const changedTypeIds = changedCardTypeIds(
			this.lastSavedLayout,
			nextCards
		);

		for (const cardId of changedTypeIds) {
			const node = this.grid.engine.nodes.find(
				candidate => candidate.id === cardId
			);
			if (node && !node.el) {
				deferredCardIds.add(cardId);
				continue;
			}
			this.destroyCard(cardId);
			if (node?.el) {
				this.grid.removeWidget(node.el, true, false);
			}
		}

		return deferredCardIds;
	}

	/**
	 * Single entry point for applying a layout snapshot to the grid — used on
	 * initial render and on every subsequent input update (local resize echo,
	 * future websocket/remote update, layout reload).
	 *
	 * Builds a `GridStackWidget[]` and hands it to `grid.load()`. GridStack does
	 * the diff itself, keyed by widget `id`:
	 *   - existing ids → `update()` (no DOM/component churn)
	 *   - new ids → `added` event fires → we create the card component
	 *   - missing ids → `removed` event fires → we destroy the card component
	 *
	 * That's why the post-resize grey flash is gone: the echo of a local resize
	 * comes back through here, all ids match, GridStack updates in place, and
	 * no Angular component is destroyed or recreated.
	 *
	 * One exception is classified here before the load: an existing `cardId`
	 * whose `cardType` changed is remounted via
	 * {@link remountCardsWithChangedType} so the correct component type is
	 * created. Position/size-only echoes still never remount.
	 */
	private applyLayout(): void {
		if (!this.grid) return;

		const layout = this.currentLayout();
		log(
			`[JepLayoutComponent] applyLayout: ${layout.cards.length} cards`,
			layout.cards.map(c => ({
				cardId: c.cardId,
				cardType: c.cardType,
				x: c.x,
				y: c.y,
				w: c.w,
				h: c.h,
			}))
		);

		const uniqueCards = dedupeLayoutCardsByCardId(layout.cards).map(item =>
			this.#normalizeCompactFilterLayoutItem(item)
		);
		const deferredRemountIds =
			this.remountCardsWithChangedType(uniqueCards);
		const widgets = uniqueCards.map(item => {
			const normalized = this.normalizeGridCoordinates(item);
			const { minW, minH } = getCardGridSize(item.cardType);
			const resolvedH = this.#resolveLayoutRowHeight(item, normalized.h);
			const controlType =
				this.#resolveFilterControlTypeForLayoutItem(item);
			const isCompactFilter =
				item.cardType === CardType.Filter &&
				isCompactFilterControlType(controlType);
			const compactMinH = compactFilterLayoutMinRowHeight(controlType);

			return {
				id: item.cardId,
				x: normalized.x,
				y: normalized.y,
				w: normalized.w,
				h: resolvedH,
				noResize: item.noResize,
				noMove: item.noMove,
				locked: item.locked,
				minW,
				minH: isCompactFilter
					? (compactMinH ?? minH)
					: (item.minH ?? minH),
				...(isCompactFilter
					? {
							maxH: resolvedH,
							resizable: { handles: 'e' },
						}
					: {}),
				content: '',
			};
		});

		this.grid.load(widgets);

		// Deferred remounts keep their previous cardType in the saved snapshot
		// (coordinates and other fields still take the incoming values) so the
		// pending type transition is re-detected and retried on the next apply.
		const previousCardById = new Map(
			this.lastSavedLayout.map(card => [card.cardId, card])
		);
		this.lastSavedLayout = uniqueCards.map(item => {
			const previous = deferredRemountIds.has(item.cardId)
				? previousCardById.get(item.cardId)
				: undefined;
			return previous
				? { ...item, cardType: previous.cardType }
				: { ...item };
		});
	}

	private createAndConfigureComponent(
		item: CardList,
		container: HTMLElement
	): void {
		this.destroyCardEffects(item.cardId);
		const registeredCardEffects: EffectRef[] = [];
		log(`[JepLayoutComponent] Creating component for ${item.cardId}`, {
			type: item.cardType,
			container,
			item, // Log the full item to see what we're getting from the layout
		});

		const componentType = this.componentMap.get(item.cardType);
		if (!componentType) {
			throw new Error(`Unsupported card type: ${item.cardType}`);
		}

		const componentRef = createComponent(componentType, {
			environmentInjector: this.environmentInjector,
			hostElement: container,
		});
		// createComponent host bindings replace the GridStack content class; spacing CSS
		// and drag handle selectors depend on it.
		container.classList.add('grid-stack-item-content');

		const instance = componentRef.instance;

		// Common configuration via setInput for signal inputs
		const currentThemeMode = this.themeService.getActiveThemeModeSignal()();
		const reportPaletteId = this.#resolvedReportThemePaletteId();
		componentRef.setInput('palette', this.effectiveReportPaletteId());
		componentRef.setInput('themeConfig', this.themeConfig());
		if (instance instanceof JeptoChartCardComponent && reportPaletteId) {
			componentRef.setInput('reportThemePaletteId', reportPaletteId);
		}
		componentRef.setInput('cardId', item.cardId);
		componentRef.setInput('themeMode', currentThemeMode);
		componentRef.setInput('debugMode', this.getDebugModeFromUrl());

		// Pass edit mode state and keep it in sync with staticGrid changes.
		// All card types extend JeptoBaseCardComponent which now carries the edit-mode API.
		componentRef.setInput('isEditMode', !this.staticGrid());
		if (instance instanceof JeptoAnalysisCardComponent) {
			componentRef.setInput('publishedOnly', this.staticGrid());
		}

		runInInjectionContext(this.environmentInjector, () => {
			registeredCardEffects.push(
				effect(() => {
					componentRef.setInput('isEditMode', !this.staticGrid());
					if (instance instanceof JeptoAnalysisCardComponent) {
						componentRef.setInput(
							'publishedOnly',
							this.staticGrid()
						);
					}
				})
			);
		});

		const sub = new Subscription();
		sub.add(
			instance.editCard.subscribe(() => this.cardEdit.emit(item.cardId))
		);
		sub.add(
			instance.duplicateCard.subscribe(() =>
				this.cardDuplicate.emit(item.cardId)
			)
		);
		sub.add(
			instance.saveAsTemplate.subscribe(() =>
				this.cardSaveAsTemplate.emit(item.cardId)
			)
		);
		sub.add(
			instance.deleteCard.subscribe(() =>
				this.cardDelete.emit(item.cardId)
			)
		);
		if (instance instanceof JeptoFilterCardComponent) {
			sub.add(
				instance.configureFilterScope.subscribe(() =>
					this.filterScopeConfigure.emit(item.cardId)
				)
			);
			// Always subscribe so edit→preview toggles work without recreating cards.
			// clients-report sets staticGrid=true in preview (interactive filter picker).
			sub.add(
				instance.dateRangeChange.subscribe(expression =>
					this.#forwardFilterDateRangeChangeWhenPreview(expression)
				)
			);
			if (item.controlType) {
				componentRef.setInput('config', {
					cardId: item.cardId,
					cardType: CardType.Filter,
					pageId: this.currentLayout().pageId,
					viewId: this.currentLayout().layoutId,
					clientId: this.userId() || '',
					controlType: item.controlType,
				});
			}
		}
		this.cardSubscriptions.get(item.cardId)?.unsubscribe();
		this.cardSubscriptions.set(item.cardId, sub);

		// Store reference for cleanup
		this.componentRefs.set(item.cardId, componentRef);

		// Get the cached signal for this card's config - use the same signal reference
		// to prevent multiple subscriptions
		const cardConfigSignal = this.cardService.cardConfig(item.cardId);

		const contentDataSignal =
			item.cardType === CardType.Html ||
			item.cardType === CardType.Markdown
				? this.dataService.contentDataSignal(item.cardId)
				: undefined;

		// Single consolidated effect for card configuration and theme updates
		// This replaces multiple separate effects that were causing excessive re-runs
		runInInjectionContext(this.environmentInjector, () => {
			registeredCardEffects.push(
				effect(() => {
					// Read both signals in the same effect to consolidate updates
					const cardConfig = cardConfigSignal();
					const currentThemeMode =
						this.themeService.getActiveThemeModeSignal()();

					// Update theme mode using setInput to properly trigger ngOnChanges
					componentRef.setInput('themeMode', currentThemeMode);
					componentRef.setInput(
						'palette',
						this.effectiveReportPaletteId()
					);
					const reportPaletteId =
						this.#resolvedReportThemePaletteId();
					if (instance instanceof JeptoChartCardComponent) {
						componentRef.setInput(
							'reportThemePaletteId',
							reportPaletteId ?? undefined
						);
					}

					const isRuntimeRefreshLoading = this.cardRuntimeRefreshState
						.loadingCardIds()
						.has(item.cardId);
					if (
						instance instanceof JeptoChartCardComponent ||
						instance instanceof JeptoTableCardComponent
					) {
						componentRef.setInput(
							'isPreviewDataLoading',
							isRuntimeRefreshLoading
						);
					}
					if (instance instanceof JeptoAnalysisCardComponent) {
						componentRef.setInput(
							'loading',
							isRuntimeRefreshLoading
						);
					}

					if (
						item.cardType === CardType.Filter &&
						instance instanceof JeptoFilterCardComponent
					) {
						const stored = cardConfig as
							| FilterCardConfig
							| undefined;
						const controlType =
							item.controlType ?? stored?.controlType;
						if (controlType) {
							const merged: FilterCardConfig = {
								...(stored ?? {}),
								cardId: item.cardId,
								cardType: CardType.Filter,
								pageId: this.currentLayout().pageId,
								viewId: this.currentLayout().layoutId,
								clientId: this.userId() || '',
								controlType,
							};
							componentRef.setInput('config', merged);
						}
					}

					if (!cardConfig) return;

					// Apply common card properties using setInput for proper change detection.
					// Icon/title/subtitle may arrive async (e.g. from getCardConfigs); this effect re-runs when config updates.
					if (cardConfig.title !== undefined) {
						componentRef.setInput('title', cardConfig.title);
					}
					if (cardConfig.subtitle !== undefined) {
						componentRef.setInput('subtitle', cardConfig.subtitle);
					}
					if (
						cardConfig.icon !== undefined &&
						cardConfig.icon !== null &&
						cardConfig.icon !== ''
					) {
						componentRef.setInput('icon', cardConfig.icon);
					}
					if (cardConfig.style) {
						componentRef.setInput('style', cardConfig.style);
					}

					if (
						item.cardType === CardType.Analysis &&
						instance instanceof JeptoAnalysisCardComponent
					) {
						const analysisConfig = cardConfig as AnalysisCardConfig;
						if (analysisConfig.analyses !== undefined) {
							componentRef.setInput(
								'analyses',
								analysisConfig.analyses
							);
						}
					}

					// Image card: pass imageRef, imageRefLight, imageRefDark so card displays when user configures in editor
					if (
						item.cardType === 'image' &&
						instance instanceof JeptoImageCardComponent
					) {
						const imgConfig = cardConfig as ImageCardConfig;
						if (imgConfig.imageRef !== undefined) {
							componentRef.setInput(
								'imageRef',
								imgConfig.imageRef ?? ''
							);
						}
						if (imgConfig.imageRefLight !== undefined) {
							componentRef.setInput(
								'imageRefLight',
								imgConfig.imageRefLight ?? ''
							);
						}
						if (imgConfig.imageRefDark !== undefined) {
							componentRef.setInput(
								'imageRefDark',
								imgConfig.imageRefDark ?? ''
							);
						}
						if (imgConfig.maxHeight !== undefined) {
							componentRef.setInput(
								'maxHeight',
								imgConfig.maxHeight ?? undefined
							);
						}
						if (imgConfig.maxWidth !== undefined) {
							componentRef.setInput(
								'maxWidth',
								imgConfig.maxWidth ?? undefined
							);
						}
					}

					// Defer CD — synchronous detectChanges here loops with mat-menu afterRender (NG0103).
					this.#scheduleComponentDetectChanges(componentRef);
				})
			);

			// Content data (HTML/Markdown only) from WebSocket via DataService.
			if (contentDataSignal) {
				const applyContent = (content: string) => {
					if (
						item.cardType === CardType.Html &&
						instance instanceof JeptoHtmlCardComponent
					) {
						componentRef.setInput('html', content);
						componentRef.setInput('loading', false);
					} else if (
						item.cardType === CardType.Markdown &&
						instance instanceof JeptoMarkdownCardComponent
					) {
						componentRef.setInput('markdown', content);
						componentRef.setInput('loading', false);
					}
					this.#scheduleComponentDetectChanges(componentRef);
				};

				const existingContent = contentDataSignal();
				if (existingContent !== undefined) {
					log(
						`[JepLayoutComponent] Found existing content for ${item.cardId}, applying immediately`
					);
					applyContent(existingContent);
				}

				registeredCardEffects.push(
					effect(() => {
						const content = contentDataSignal();
						if (content === undefined) return;
						applyContent(content);
					})
				);
			}

			// Separate effect for filter data (Filter cards)
			if (
				item.cardType === 'filter' &&
				instance instanceof JeptoFilterCardComponent
			) {
				const filterDataSignal = this.dataService.filterDataSignal(
					item.cardId
				);
				let lastFilterDataFingerprint = '';
				const applyFilterData = (filterData: FilterCardRuntimeData) => {
					let payload = filterData;
					const config = this.cardService.cardConfig(item.cardId)();
					if (
						isFilterCardConfig(config) &&
						config.controlType === ControlType.Dropdown &&
						isDimensionFilterCardData(filterData)
					) {
						payload = mergeDimensionFilterPreviewData(
							filterData,
							config
						);
					}
					const fingerprint = isDimensionFilterCardData(payload)
						? dimensionFilterDataFingerprint(payload)
						: JSON.stringify(payload);
					if (
						fingerprint !== '' &&
						fingerprint === lastFilterDataFingerprint
					) {
						return;
					}
					lastFilterDataFingerprint = fingerprint;
					componentRef.setInput('filterData', payload);
					this.#scheduleComponentDetectChanges(componentRef);
				};

				registeredCardEffects.push(
					effect(() => {
						componentRef.setInput(
							'reportDateRangeExpression',
							this.reportDateRangeExpression()
						);
					})
				);

				const existingFilterData = filterDataSignal();
				if (existingFilterData !== undefined) {
					log(
						`[JepLayoutComponent] Found existing filter data for ${item.cardId}, applying immediately`
					);
					applyFilterData(existingFilterData);
				}

				registeredCardEffects.push(
					effect(() => {
						const filterData = filterDataSignal();
						if (filterData === undefined) return;
						applyFilterData(filterData);
					})
				);
			}

			// Image cards: bridge DataService WebSocket image data to component
			// inputs via explicit effect + deferred CD — same pattern as
			// HTML, markdown, and filter cards.
			if (
				item.cardType === 'image' &&
				instance instanceof JeptoImageCardComponent
			) {
				const imageSignal = this.dataService.imageDataSignal(
					item.cardId
				);

				const applyImageData = (data: ImageDataRefs) => {
					if (!data) return;
					if (data.imageRef !== undefined) {
						componentRef.setInput('imageRef', data.imageRef ?? '');
					}
					if (data.imageRefLight !== undefined) {
						componentRef.setInput(
							'imageRefLight',
							data.imageRefLight ?? ''
						);
					}
					if (data.imageRefDark !== undefined) {
						componentRef.setInput(
							'imageRefDark',
							data.imageRefDark ?? ''
						);
					}
					this.#scheduleComponentDetectChanges(componentRef);
				};

				// Apply any data that arrived before this effect was created
				const existing = imageSignal();
				if (existing) {
					applyImageData(existing);
				}

				registeredCardEffects.push(
					effect(() => {
						const data = imageSignal();
						if (!data) return;
						applyImageData(data);
					})
				);
			}
		});

		// For image cards, force initial render
		if (item.cardType === 'image') {
			this.#scheduleComponentDetectChanges(componentRef);
		}

		// For chart cards, set up chart-specific configuration
		if (
			item.cardType === 'chart' &&
			instance instanceof JeptoChartCardComponent
		) {
			const chartInstance = instance as JeptoChartCardComponent;
			const chartConfig: ChartCardConfig = {
				cardId: item.cardId,
				chartType: item.chartType || ChartType.Line,
				cardType: CardType.Chart,
				pageId: this.currentLayout().pageId,
				viewId: this.currentLayout().layoutId,
				clientId: this.userId() || '',
			};
			componentRef.setInput('config', chartConfig);
			componentRef.setInput('allowEmptyChart', this.allowEmptyCharts());

			// Get the reactive signal for this card's chart data
			// Using cardDataSignal() returns a cached Signal that updates when data arrives via WebSocket
			const cardDataSignal = this.dataService.cardDataSignal(item.cardId);

			// Helper to apply chart data using setInput for proper change detection
			// Query result is compatible with ChartData on chart / tableData on table
			// The chart card handles the generic structure, and individual chart components
			// extract what they need (series for line/bar/area/column/scatter, donutSeries for donut)
			const applyChartData = (
				chartData: QueryCardDataResponse | null | undefined
			) => {
				if (!chartData) return;

				// Pass query payload directly
				// which has data?: { series?: any[]; donutSeries?: any[]; }
				// Note: loading is a computed signal in the chart card, not an input
				componentRef.setInput('data', chartData);
				this.#scheduleComponentDetectChanges(componentRef);
			};

			// Check for existing data BEFORE setting up the effect
			// This handles the race condition where data arrives before the component effect is created
			const existingChartData = cardDataSignal();
			if (existingChartData) {
				log(
					`[JepLayoutComponent] Found existing chart data for ${item.cardId}, applying immediately`
				);
				applyChartData(existingChartData);
			}

			// Set up effect for future chart data updates
			runInInjectionContext(this.environmentInjector, () => {
				registeredCardEffects.push(
					effect(() => {
						const chartData = cardDataSignal();
						if (chartData) {
							applyChartData(chartData);
						}
					})
				);
			});
		}

		// Table cards: config from CardService; slices via DataService + Grid fetchCallback
		if (
			item.cardType === CardType.Table &&
			instance instanceof JeptoTableCardComponent
		) {
			const tableCardConfigSignal = this.cardService.cardConfig(
				item.cardId
			);

			const shellConfig = (): TableCardConfig => ({
				cardId: item.cardId,
				cardType: CardType.Table,
				pageId: this.currentLayout().pageId,
				viewId: this.currentLayout().layoutId,
				clientId: this.userId() || '',
			});

			const applyTableConfig = () => {
				const stored = tableCardConfigSignal();
				const merged: TableCardConfig = {
					...shellConfig(),
					...(stored as TableCardConfig | undefined),
					cardId: item.cardId,
					cardType: CardType.Table,
				};
				componentRef.setInput('config', merged);
				this.#scheduleComponentDetectChanges(componentRef);
			};

			applyTableConfig();

			runInInjectionContext(this.environmentInjector, () => {
				registeredCardEffects.push(effect(() => applyTableConfig()));
			});
		}

		if (registeredCardEffects.length > 0) {
			this.cardEffectRefs.set(item.cardId, registeredCardEffects);
		}

		this.appRef.attachView(componentRef.hostView);

		if (this.#shouldFlushInitialCardRender(item)) {
			this.#detectComponentChanges(componentRef);
		}
	}

	ngOnDestroy() {
		this.destroyed = true;
		this.cancelGridInitFrame();

		// Clean up event listeners
		window.removeEventListener('resize', this.resizeHandler);
		window.removeEventListener(
			'orientationchange',
			this.orientationHandler
		);
		this.grid?.el?.removeEventListener(
			'pointerdown',
			this.gridPointerDownHandler,
			true
		);
		this.endLiftedTransforms();

		// Clean up subscriptions, signal effects, and component references
		this.destroyAllCardEffects();
		this.cardSubscriptions.forEach(sub => sub.unsubscribe());
		this.cardSubscriptions.clear();
		this.componentRefs.forEach(ref => {
			ref.destroy();
		});
		this.componentRefs.clear();

		// Clean up effect
		if (this.effectRef) {
			this.effectRef.destroy();
		}

		// Clean up grid
		if (this.grid) {
			this.grid.destroy();
			this.grid = undefined;
		}

		this.gridLayoutStyleEl?.remove();
		this.gridLayoutStyleEl = null;
	}

	private handleCardError(itemId: string, error: any) {
		this.errorStates.set(itemId, true);

		// Log the card error details
		loggerError(`[JepLayoutComponent] Error in card ${itemId}:`, {
			message: error.message || error,
			stack: error.stack,
			type: 'CardError',
		});

		this.cardError.emit({
			itemId,
			error: {
				message: error.message || error,
				type: 'CardError',
			},
		});
	}

	/**
	 * Defer card CD so Material afterRender hooks (e.g. mat-menu) are not re-entered
	 * synchronously from an effect — that pattern triggers NG0103 on filter cards.
	 */
	#scheduleComponentDetectChanges(componentRef: ComponentRef<unknown>): void {
		queueMicrotask(() => {
			this.#detectComponentChanges(componentRef);
		});
	}

	#detectComponentChanges(componentRef: ComponentRef<unknown>): void {
		try {
			componentRef.changeDetectorRef.detectChanges();
		} catch {
			// Component destroyed before change detection ran.
		}
	}

	#shouldFlushInitialCardRender(item: CardList): boolean {
		return (
			item.cardType === CardType.Chart || item.cardType === CardType.Table
		);
	}

	private applyResponsiveTypography(screenWidth: number) {
		const themeConfig = this.themeService.getThemeConfig();
		const baseFontUnit =
			themeConfig.typography.baseSize.match(/[a-zA-Z]+/)?.[0] || 'px';
		let baseFontSize = parseFloat(
			themeConfig.typography.baseSize.replace(baseFontUnit, '')
		);

		// Scale font size based on screen width
		if (screenWidth <= 480) {
			baseFontSize = Math.max(baseFontSize * 0.9, 14);
		} else if (screenWidth <= 768) {
			baseFontSize = Math.max(baseFontSize * 0.95, 15);
		} else if (screenWidth <= 1024) {
			baseFontSize *= 0.975;
		}

		// Apply font sizes
		const sizeMappings = {
			xxs: themeConfig.typography.size.xxs,
			xs: themeConfig.typography.size.xs,
			sm: themeConfig.typography.size.sm,
			md: themeConfig.typography.size.md,
			lg: themeConfig.typography.size.lg,
			xl: themeConfig.typography.size.xl,
			xxl: themeConfig.typography.size.xxl,
		};

		Object.entries(sizeMappings).forEach(([key, value]) => {
			document.documentElement.style.setProperty(
				`--font-size-${key}`,
				`${value * baseFontSize}${baseFontUnit}`
			);
		});

		// Calculate and apply line heights
		const lineHeightFactor = screenWidth <= 768 ? 0.95 : 1;
		const lineHeightMappings = {
			tight: themeConfig.typography.lineHeight.tight,
			normal: themeConfig.typography.lineHeight.normal,
			loose: themeConfig.typography.lineHeight.loose,
			double: themeConfig.typography.lineHeight.double,
		};

		Object.entries(lineHeightMappings).forEach(([key, value]) => {
			document.documentElement.style.setProperty(
				`--line-height-${key}`,
				`${value * baseFontSize * lineHeightFactor}${baseFontUnit}`
			);
		});
	}

	#resolvedReportThemePaletteId(): string | undefined {
		const explicit = this.reportThemePaletteId()?.trim();
		if (explicit) {
			return explicit;
		}
		return this.themeConfig()?.palette?.trim() || undefined;
	}
}
